import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { setHistorySelectedTime, setTransportConditionSelectedItem } from 'app/actions/map';
import { Autosizer, Scrollbar } from 'app/components';
import config from 'app/config';
import { useAsyncAction } from 'app/services';
import { hdopLoad } from 'app/store/map';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const MapTransportConditionList = forwardRef(function (props, ref) {
  const dispatch = useDispatch();
  const listRef = useRef(null);
  const scrollableNodeRef = useRef(null);
  const mapRef = useSelector((state) => state.map.ref, shallowEqual);
  const conditionItems = useSelector((state) =>
    state.map.transportSelectedItem.conditions.filter((condition) => condition.type === 'message')
  );
  const conditionSelectedItem = useSelector((state) => state.map.transportConditionSelectedItem);
  const conditionSelectedDatetime = useSelector((state) => state.map.transportConditionSelectedDatetime);
  const checkedType = useSelector((state) => state.map.transportConditionFilterCheckedType);
  const checkedItems = useSelector((state) => state.map.transportConditionFilterCheckedItems);
  const { isHistoryOpen } = useSelector((state) => state.map);
  const { asyncAction: fetchHdopData } = useAsyncAction(hdopLoad);

  const items = useMemo(() => {
    return conditionItems.reduce((accumulator, currentValue) => {
      const currentElement = {
        ...currentValue,
        messages:
          checkedType !== 'all'
            ? currentValue.messages.some((message) => checkedItems.indexOf(message.key) !== -1)
              ? currentValue.messages
              : []
            : currentValue.messages,
      };

      if (currentElement.messages.length !== 0) {
        return [...accumulator, currentElement];
      }

      if (checkedItems.indexOf('other:message') !== -1 || checkedType === 'all') {
        return [...accumulator, currentElement];
      }

      return accumulator;
    }, []);
  }, [conditionItems, checkedItems, checkedType]);

  useEffect(() => {
    if (checkedType !== 'all') {
      if (!checkedItems) {
        dispatch(setTransportConditionSelectedItem(items.at(-1)));
      }
      if (checkedItems) {
        const selectedIndex = items.findIndex((item) => item.datetime === conditionSelectedItem.datetime);
        if (selectedIndex < 0) {
          dispatch(setTransportConditionSelectedItem(items.at(-1)));
        }
      }
    }
    if (checkedItems && listRef.current) {
      const selectedIndex = items.findIndex((item) => item?.datetime === conditionSelectedItem?.datetime);
      if (selectedIndex > 0) {
        setTimeout(() => {
          listRef.current?.scrollToItem(selectedIndex, 'center');
        }, 100);
      }
    }
  }, [checkedType]);

  const itemSelectedIndex = useMemo(() => {
    if (!conditionSelectedItem) return -1;
    setHistorySelectedTime(conditionSelectedItem?.datetime);
    return items.findIndex((item) => item?.datetime === conditionSelectedItem?.datetime);
  }, [items, conditionSelectedItem]);

  const handleItemClick = useCallback(
    (value = null, cb) => {
      if (value?.datetime === conditionSelectedItem?.datetime) return;
      if (value && !items.some((item) => item.datetime === value.datetime)) return;
      dispatch(setTransportConditionSelectedItem(value));

      if (value && value.data.geodata !== 'ERR') {
        mapRef.current.handleClosePopup();
        mapRef.current.handleFlyTo([value.data.lat, value.data.lon]);
      }
      if (isHistoryOpen) {
        dispatch(setHistorySelectedTime(value?.datetime));
      }

      if (cb) {
        cb();
      }
    },
    [items, conditionSelectedItem?.datetime, mapRef]
  );
  useImperativeHandle(
    ref,
    () => ({
      handleKeyUp() {
        if (items[itemSelectedIndex - 1]) {
          handleItemClick(items[itemSelectedIndex - 1], () => {
            listRef.current?.scrollToItem(itemSelectedIndex - 1, 'end');
          });
        }
      },
      handleKeyDown() {
        if (items[itemSelectedIndex + 1]) {
          handleItemClick(items[itemSelectedIndex + 1], () => {
            listRef.current?.scrollToItem(itemSelectedIndex + 1, 'end');
          });
        }
      },
    }),
    [listRef, itemSelectedIndex]
  );

  useEffect(() => {
    if (conditionSelectedItem?.hdopData) {
      mapRef.current.handleClosePopup();
      mapRef.current.handleFlyTo([conditionSelectedItem.hdopData.lat, conditionSelectedItem.hdopData.lon]);
    }
  }, [conditionSelectedItem]);

  useEffect(() => {
    let isMounted = true;

    const processSelection = async () => {
      if (conditionSelectedDatetime) {
        const index = items.findIndex((i) => i.datetime === conditionSelectedDatetime);
        if (index !== -1 && isMounted) {
          handleItemClick(items[index], () => {
            listRef.current?.scrollToItem(index, 'center');
          });
          return;
        }
      }

      if (items.length > 0 && !conditionSelectedItem && isMounted) {
        handleItemClick(items[items.length - 1], () => {
          listRef.current?.scrollToItem(items.length - 1, 'end');
        });
      }
    };

    processSelection();

    return () => {
      isMounted = false;
    };
  }, [conditionSelectedDatetime, items.length]);

  const prevCountRef = useRef();

  useEffect(() => {
    if (items[itemSelectedIndex]?.data.hdop > config.messageHdopLimit || items[itemSelectedIndex]?.data.geodata === 'ERR') {
      fetchHdopData({
        datetime: items[itemSelectedIndex].datetime,
        parking_time: items[itemSelectedIndex].data.parking_time,
      });
    }
    if (prevCountRef.current + 1 === items.length - 1) {
      listRef.current?.scrollToItem(items.length - 1, 'end');
    }
    prevCountRef.current = itemSelectedIndex;
  }, [itemSelectedIndex]);

  // Прокручиваем список к выбранному элементу при изменении conditionSelectedItem
  // только если элемент находится вне поля видимости (только в режиме "машина времени")
  useEffect(() => {
    if (isHistoryOpen && conditionSelectedItem && listRef.current && items.length > 0) {
      const selectedIndex = items.findIndex((item) => item?.datetime === conditionSelectedItem?.datetime);
      if (selectedIndex >= 0) {
        setTimeout(() => {
          const list = listRef.current;
          const scrollableNode = scrollableNodeRef.current;

          if (list && scrollableNode) {
            const itemSize = 24;
            const scrollTop = scrollableNode.scrollTop || 0;
            const visibleHeight = scrollableNode.clientHeight || 0;

            // Вычисляем видимый диапазон индексов
            const firstVisibleIndex = Math.max(0, Math.floor(scrollTop / itemSize));
            const lastVisibleIndex = Math.min(items.length - 1, Math.ceil((scrollTop + visibleHeight) / itemSize));

            // Прокручиваем только если элемент вне поля видимости
            if (selectedIndex < firstVisibleIndex || selectedIndex > lastVisibleIndex) {
              list.scrollToItem(selectedIndex, 'center');
            }
          } else if (list) {
            // Fallback: если не удалось получить scrollableNode, используем 'smart'
            list.scrollToItem(selectedIndex, 'smart');
          }
        }, 100);
      }
    }
  }, [isHistoryOpen, conditionSelectedItem?.datetime, items]);

  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          margin: theme.spacing(0),
          alignItems: 'center',
          height: '100%',
          position: 'relative',
          flex: 'none',
          '&:nth-of-type(1)': {
            flex: '0 0 35%',
            padding: '3px 8px 3px 12px',
          },
          '&:nth-of-type(2)': {
            flex: '1 1 65%',
            padding: '3px 12px 3px 8px',
          },
        },
        outline: 'none',
        '.simplebar-container': {
          maxHeight: 'none',
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <Scrollbar>
              {({ scrollableNodeRef: scrollableRef, contentNodeRef }) => {
                scrollableNodeRef.current = scrollableRef?.current;
                return (
                  <FixedSizeList
                    ref={listRef}
                    height={height}
                    itemSize={24}
                    overscanCount={20}
                    itemCount={items.length}
                    innerRef={contentNodeRef}
                    outerRef={scrollableRef}
                    innerElementType={MapTransportConditionListInnerElementType}
                    initialScrollOffset={itemSelectedIndex * 24 > height ? itemSelectedIndex * 24 : 0}
                    itemData={{
                      items,
                      selectedItem: conditionSelectedItem,
                      onClick: handleItemClick,
                    }}>
                    {MapTransportConditionListItemRender}
                  </FixedSizeList>
                );
              }}
            </Scrollbar>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
});

const MapTransportConditionListInnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <List ref={ref} style={style} sx={{ minHeight: '100%' }} {...rest}>
    <ListItem
      disablePadding
      sx={(theme) => ({
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: style.height,
        minHeight: '100%',
        zIndex: '2',
        pointerEvents: 'none',
        padding: theme.spacing(0),
        '.MuiListItemText-root': {
          '&:before': {
            content: '""',
            position: 'absolute',
            top: '0',
            right: '0',
            height: '100%',
            bgcolor: theme.palette.divider,
            width: '1px',
            zIndex: '1',
            pointerEvents: 'none',
          },
          '&:nth-of-type(1) .MuiTypography-root': {
            opacity: '0',
            visibility: 'hidden',
          },
          '&:nth-of-type(2):before': {
            display: 'none',
          },
        },
      })}>
      <ListItemText>
        <Typography component='span' />
      </ListItemText>
      <ListItemText style={{ flex: '1 1 auto' }} />
    </ListItem>
    {children}
  </List>
));

const MapTransportConditionListItemRender = ({ data, index, style }) => {
  const { items, onClick, selectedItem } = data;
  const isSuccess = items[index].messages.some((message) => ['guard_out8:on', 'guard_out8:off'].includes(message.key));
  const isAlert = items[index].messages.some((message) => ['guard_status8:on', 'guard_status8:off'].includes(message.key));
  const isWarning = items[index].messages.some((message) => ['event_id:1'].includes(message.key));

  return (
    <ListItem
      disablePadding
      key={index}
      style={style}
      selected={selectedItem?.datetime === items[index].datetime}
      onClick={() => onClick(items[index])}
      sx={(theme) => ({
        '&:hover': {
          cursor: 'pointer',
        },
        '&.Mui-selected': {
          bgcolor: theme.palette.primary.main + '!important',
          color: theme.palette.primary.contrastText,
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root:nth-of-type(2) span:last-of-type': {
          paddingLeft: '6px',
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          bgcolor: 'background.default',
        }),
        ...(isSuccess && {
          bgcolor: '#aaffaa',
        }),
        ...(isAlert && {
          bgcolor: '#ffaaaa',
        }),
        ...(isWarning && {
          bgcolor: '#ffc107',
        }),
      })}>
      <ListItemText disableTypography>
        <Typography component='span'>{items.length - (items.length - 1 - index)}</Typography>
      </ListItemText>
      <ListItemText disableTypography>
        <Typography component='span'>{moment(items[index].datetime).format('DD.MM')}</Typography>
        <Typography component='span'>{moment(items[index].datetime).format('HH:mm:ss')}</Typography>
      </ListItemText>
    </ListItem>
  );
};

export default MapTransportConditionList;
